import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useApp } from '@/context/AppContext';
import { Event, Project, ProjectContact } from '@/types';
import { projectService, toContactInput } from '@/services/projectService';
import { toast } from '@/hooks/use-toast';
import {
  ArrowRight,
  MapPin,
  Plus,
  CheckCircle2,
  Loader2,
  Power,
  Save
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { he } from 'date-fns/locale';
import NewDocumentModal from '@/components/NewDocumentModal';
import DocumentsTab from '@/components/DocumentsTab';
import EventDetailsModal from '@/components/EventDetailsModal';
import EventChangeLogDialog from '@/components/EventChangeLogDialog';
import ProjectContactsEditor from '@/components/ProjectContactsEditor';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import AddTask from '@/components/AddTask';
import BusyIndicator from '@/components/BusyIndicator';
import { useProjectEvents } from '@/hooks/useProjectEvents';
import ProjectQuickActions from '@/components/project-details/ProjectQuickActions';
import ProjectTimeline from '@/components/project-details/ProjectTimeline';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';

const tabs = [
  { id: 'timeline', label: 'ציר זמן' },
  { id: 'details', label: 'פרטים' },
  { id: 'files', label: 'קבצים' },
];

export default function ProjectDetails() {
  const { id, eventId } = useParams<{ id: string; eventId?: string }>();
  const navigate = useNavigate();
  const { projects, getProjectTasks, addTask, toggleTask, updateEvent, saveToLocalStorage, updateProject } = useApp();
  const { events, isLoading, reloadEvents } = useProjectEvents(id);
  const [activeTab, setActiveTab] = useState('timeline');
  const [groupTimelineByDay, setGroupTimelineByDay] = useState(false);
  const [timelineDateFrom, setTimelineDateFrom] = useState('');
  const [timelineDateTo, setTimelineDateTo] = useState('');
  const [timelineTypeFilter, setTimelineTypeFilter] = useState<Record<Event['type'], boolean>>({
    visit: true,
    progress: true,
    fault: true,
  });
  const [showNewDoc, setShowNewDoc] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [historyEvent, setHistoryEvent] = useState<Event | null>(null);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [showAddTask, setShowAddTask] = useState(false);
  const [project, setProject] = useState<Project | null>(null);
  const [isSavingChange, setIsSavingChange] = useState(false);
  const [editContacts, setEditContacts] = useState<ProjectContact[]>([]);
  const [isSavingContacts, setIsSavingContacts] = useState(false);
  const [showContactErrors, setShowContactErrors] = useState(false);

  useEffect(() => {
    if (id) {
      const found = projects.find(p => p.id === id) || null;
      setProject(found);
      if (found) {
        if (found.contacts && found.contacts.length > 0) {
          setEditContacts(found.contacts);
        } else if (found.phone) {
          setEditContacts([{ name: 'איש קשר ראשי', phone: found.phone }]);
        } else {
          setEditContacts([]);
        }
      }
    }
  }, [id]);

  // Sync the open modal with the :eventId URL param (deep linking).
  useEffect(() => {
    if (!eventId) {
      setSelectedEvent(null);
      return;
    }
    const found = events.find(e => e.id === eventId) || null;
    setSelectedEvent(found);
  }, [eventId, events]);

  const openEvent = (event: Event) => {
    navigate(`/projects/${id}/${event.id}`);
  };

  const closeEvent = () => {
    navigate(`/projects/${id}`);
  };

  const displayContacts = useMemo<ProjectContact[]>(() => {
    if (!project) return [];
    if (project.contacts && project.contacts.length > 0) return project.contacts;
    if (project.phone) return [{ name: 'איש קשר ראשי', phone: project.phone }];
    return [];
  }, [project]);

  // Combined inclusive date-range + event-type filter on the timeline; compare
  // yyyy-MM-dd strings to avoid time-of-day issues. Preserves the incoming sort order.
  const filteredTimelineEvents = useMemo<Event[]>(() => {
    return events.filter((event) => {
      if (!timelineTypeFilter[event.type]) return false;
      const day = format(new Date(event.date), 'yyyy-MM-dd');
      if (timelineDateFrom && day < timelineDateFrom) return false;
      if (timelineDateTo && day > timelineDateTo) return false;
      return true;
    });
  }, [events, timelineDateFrom, timelineDateTo, timelineTypeFilter]);

  const clearTimelineFilter = () => {
    setTimelineDateFrom('');
    setTimelineDateTo('');
  };

  const tasks = getProjectTasks(id || '');

  const handleFaultCompleted = (eventId: string) => {
    updateEvent(eventId, { completed: true });
  }

  const onSaveContacts = async () => {
    if (!id) return;
    const hasInvalid = editContacts.some(c => !c.name.trim() || !c.phone.trim());
    if (hasInvalid) {
      setShowContactErrors(true);
      return;
    }
    setShowContactErrors(false);
    setIsSavingContacts(true);
    try {
      const payload: Record<string, unknown> = { contacts: editContacts.map(toContactInput) };
      if (editContacts.length > 0) {
        payload.Phone = editContacts[0].phone;
      }
      const updated = await projectService.updateProject(id, payload);
      const nextContacts: ProjectContact[] = updated?.contacts ?? editContacts;
      const nextPhone = editContacts.length > 0 ? editContacts[0].phone : project?.phone;
      updateProject(id, { contacts: nextContacts, phone: nextPhone });
      setProject(prev => prev ? { ...prev, contacts: nextContacts, phone: nextPhone } : prev);
      toast({
        title: 'אנשי הקשר נשמרו',
        description: 'רשימת אנשי הקשר עודכנה',
      });
    } catch (e) {
      toast({
        title: 'שגיאה בשמירת אנשי הקשר',
        description: 'אירעה שגיאה בעדכון אנשי הקשר',
        variant: 'destructive',
      });
    } finally {
      setIsSavingContacts(false);
    }
  };

  const onActiveProject = async () => {
    if (!id) return;
    setIsSavingChange(true);
    
    try {
      await projectService.updateProject(id, { status: 'active' });
      setProject({...project, status: 'active'} as Project);
      projects.find(p => p.id === id)!.status = 'active';
      saveToLocalStorage();
      toast({
        title: 'הפרויקט הופעל בהצלחה',
        description: 'סטטוס הפרויקט עודכן לפעיל',
      });
      setIsSavingChange(false);
    } catch (error) {
      setIsSavingChange(false);
      toast({
        title: 'שגיאה בהפעלת הפרויקט',
        description: 'אירעה שגיאה בעדכון הפרויקט',
        variant: 'destructive'
      });
    }
  }

  if (!project) {
    return (
      <div className="p-8 text-center">
        <p className="text-muted-foreground">הפרויקט לא נמצא</p>
        <Link to="/projects" className="text-primary mt-4 inline-block">חזרה לרשימה</Link>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 md:top-0 z-30">
        <div className="p-4 md:p-6">
          <div className="flex items-center gap-3 mb-4">
            <Link
              to="/projects"
              className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-secondary transition-colors"
            >
              <ArrowRight className="w-5 h-5" />
            </Link>
            <div className="flex-1 min-w-0">
              <h1 className="font-bold text-xl truncate">{project.name}</h1>
              <p className="text-muted-foreground text-sm">{project.clientName}</p>
            </div>
          </div>

          {/* Quick Actions */}
          <ProjectQuickActions
            address={project.address}
            contacts={displayContacts}
            onNewDoc={() => setShowNewDoc(true)}
          />

          {/* Tabs */}
          <div className="flex gap-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  "flex-1 py-2.5 rounded-xl text-sm font-medium transition-all",
                  activeTab === tab.id
                    ? "bg-primary text-primary-foreground"
                    : "bg-secondary text-secondary-foreground hover:bg-secondary/80"
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="p-4 md:p-6">
        {activeTab === 'timeline' && (
          <>
            <div className="flex flex-wrap items-end gap-3 mb-4">
              <div className="flex items-center gap-2">
                <Switch
                  id="group-timeline-by-day"
                  checked={groupTimelineByDay}
                  onCheckedChange={setGroupTimelineByDay}
                />
                <label
                  htmlFor="group-timeline-by-day"
                  className={cn(
                    "text-sm cursor-pointer",
                    groupTimelineByDay ? "text-primary font-medium" : "text-muted-foreground"
                  )}
                >
                  קיבוץ לפי יום
                </label>
              </div>
              <div className="flex items-end gap-3">
                <div className="flex flex-col gap-1">
                  <label htmlFor="timeline-date-from" className="text-xs text-muted-foreground">מתאריך</label>
                  <input
                    id="timeline-date-from"
                    type="date"
                    value={timelineDateFrom}
                    max={timelineDateTo || undefined}
                    onChange={(e) => setTimelineDateFrom(e.target.value)}
                    className="w-full min-w-0 bg-card border border-border rounded-lg px-3 py-1.5 text-sm"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label htmlFor="timeline-date-to" className="text-xs text-muted-foreground">עד תאריך</label>
                  <input
                    id="timeline-date-to"
                    type="date"
                    value={timelineDateTo}
                    min={timelineDateFrom || undefined}
                    onChange={(e) => setTimelineDateTo(e.target.value)}
                    className="w-full min-w-0 bg-card border border-border rounded-lg px-3 py-1.5 text-sm"
                  />
                </div>
              </div>
              {(timelineDateFrom || timelineDateTo) && (
                <button
                  onClick={clearTimelineFilter}
                  className="text-sm text-primary font-medium py-1.5"
                >
                  נקה סינון
                </button>
              )}
              <div className="flex items-center gap-4 py-1.5">
                {([
                  { type: 'visit', label: 'ביקור באתר' },
                  { type: 'progress', label: 'שאלה' },
                  { type: 'fault', label: 'תקלה' },
                ] as { type: Event['type']; label: string }[]).map(({ type, label }) => (
                  <div key={type} className="flex items-center gap-2">
                    <Checkbox
                      id={`timeline-type-${type}`}
                      checked={timelineTypeFilter[type]}
                      onCheckedChange={(checked) =>
                        setTimelineTypeFilter((prev) => ({ ...prev, [type]: checked === true }))
                      }
                    />
                    <label htmlFor={`timeline-type-${type}`} className="text-sm cursor-pointer">
                      {label}
                    </label>
                  </div>
                ))}
              </div>
            </div>
            <ProjectTimeline
              events={filteredTimelineEvents}
              onSelectEvent={openEvent}
              onAddFirst={() => setShowNewDoc(true)}
              onFaultCompleted={handleFaultCompleted}
              onViewHistory={setHistoryEvent}
              groupTimelineByDay={groupTimelineByDay}
            />
          </>
        )}

        {activeTab === 'details' && (
          <div className="space-y-6">
            {/* Map Placeholder */}
            <div className="bg-secondary rounded-2xl h-48 flex items-center justify-center">
              <div className="text-center text-muted-foreground">
                <MapPin className="w-8 h-8 mx-auto mb-2" />
                <p className="text-sm">{project.address}</p>
              </div>
            </div>
            {project.status === "planning" && (

              <div className='space-y-3'>
                <button
                  onClick={onActiveProject}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-xl gradient-primary text-primary-foreground font-medium hover:opacity-90 transition-opacity"
                >
                  <span>הפעלת פרוייקט</span>
                  <Power className="w-4 h-4" />
                  {isSavingChange && <Loader2 className="w-4 h-4 animate-spin" />}
                </button>
              </div>
            )}
            {/* Info Cards */}
            <div className="space-y-3">
              <div className="bg-card rounded-xl p-4 border border-border">
                <p className="text-xs text-muted-foreground mb-1">כתובת</p>
                <p className="font-medium">{project.address}</p>
              </div>
              <div className="bg-card rounded-xl p-4 border border-border">
                <p className="text-xs text-muted-foreground mb-1">תאריך התחלה</p>
                <p className="font-medium">{format(new Date(project.startDate), 'd בMMMM yyyy', { locale: he })}</p>
              </div>
              <div className="bg-card rounded-xl p-4 border border-border">
                <p className="text-xs text-muted-foreground mb-1">לקוח</p>
                <p className="font-medium">{project.clientName}</p>
                {displayContacts.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {displayContacts.map((c, i) => (
                      <li key={c.id ?? i} className="flex items-center justify-between gap-2 text-sm">
                        <span className="text-muted-foreground truncate">
                          {c.name}{c.role ? ` · ${c.role}` : ''}
                        </span>
                        <a href={`tel:${c.phone}`} className="text-primary">{c.phone}</a>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {/* Contacts edit card */}
            <div className="bg-card rounded-xl p-4 border border-border">
              <Accordion type="single" collapsible>
                <AccordionItem value="contacts">
                  <AccordionTrigger>אנשי קשר</AccordionTrigger>
                  <AccordionContent>
                    <ProjectContactsEditor
                      contacts={editContacts}
                      onChange={setEditContacts}
                      showErrors={showContactErrors}
                    />
                    <Button
                      onClick={onSaveContacts}
                      disabled={isSavingContacts}
                      className="mt-3 gap-1"
                    >
                      {isSavingContacts ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4" />
                      )}
                      שמור שינויים
                    </Button>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>

            {/* Tasks */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold">משימות</h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAddTask(true)}
                  className="gap-1"
                >
                  <Plus className="w-4 h-4" />
                  הוסף משימה
                </Button>
              </div>

              {tasks.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground bg-card rounded-xl border border-border">
                  <p className="text-sm">אין משימות בפרויקט זה</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {tasks.map((task) => (
                    <div
                      key={task.id}
                      className="bg-card rounded-xl p-4 border border-border flex items-center gap-3"
                    >
                      <button
                        onClick={() => toggleTask(task.id)}
                        className={cn(
                          "w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors flex-shrink-0",
                          task.isCompleted ? "bg-success border-success" : "border-primary hover:bg-primary/10"
                        )}
                      >
                        {task.isCompleted && <CheckCircle2 className="w-4 h-4 text-success-foreground" />}
                      </button>
                      <div className="flex-1 min-w-0">
                        <p className={cn("font-medium", task.isCompleted && "line-through text-muted-foreground")}>
                          {task.title}
                        </p>
                        {task.notes && (
                          <p className="text-xs text-muted-foreground mt-0.5">{task.notes}</p>
                        )}
                        <p className="text-xs text-muted-foreground mt-1">
                          {format(new Date(task.scheduledDate), 'd/M/yy')}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleTask(task.id)}
                        className={cn(
                          "text-xs flex-shrink-0",
                          task.isCompleted ? "text-muted-foreground" : "text-primary"
                        )}
                      >
                        {task.isCompleted ? 'בטל בוצע' : 'סמן כבוצע'}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'files' && (
          <DocumentsTab projectId={id || ''} />
        )}
      </div>

      <NewDocumentModal
        open={showNewDoc}
        editEvent={editingEvent}
        onClose={(success) => {
          setShowNewDoc(false);
          setEditingEvent(null);
          if (success) reloadEvents();
        }}
        reloadEvents={reloadEvents}
        preselectedProjectId={id}
      />

      <EventDetailsModal
        event={selectedEvent}
        open={!!selectedEvent}
        onClose={closeEvent}
        onEdit={(ev) => {
          closeEvent();
          setEditingEvent(ev);
          setShowNewDoc(true);
        }}
      />

      <EventChangeLogDialog
        eventId={historyEvent?.id ?? null}
        open={!!historyEvent}
        onClose={() => setHistoryEvent(null)}
      />
      {/* Add Task Modal */}
      {showAddTask && (
        <AddTask
          open={showAddTask}
          onClose={() => setShowAddTask(false)}
          preselectedProjectId={id}
        />)}

      {isLoading && <BusyIndicator />}
    </div>
  );
}
